import React, { useContext, useEffect } from 'react';
import * as mobxReact from 'mobx-react';
import { SubWindowsHost } from '../../global/sub-window/sub-windows-host';
import { Accounts } from '../../_accounts/accounts';
import { DefaultSpinner } from '../../global/spinner/default-spinner';
import DefaultButton from '../../global/default-button/default-button';
import { EventDetailsContext } from '../../global.context';
import { IProps as IRestrictedContentProps, RestrictedContent } from '../../_restricted-content/restricted-content';
import { Permission } from '../../../api/roles.dao';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { ManageEventPages, ManageEventViewModel } from './manage-event.viewmodel';
import { MeetingsList } from './_meetings-list/meetings-list.presentation';
import './manage-event.scss';
import { RoomsList } from './_rooms/rooms';
import { ConnectionsList } from './_connections/connections.presentation';
import { OrganizationsList } from './_organizations-list/organizations-list';
import { AttendeesList } from './_attendees-list/attendees-list';
import { EditWebsite } from './_edit-website/edit-website.presentation';
import ControlListPresentation from './_control/control';
import { SalesforceOrderControl } from './shared/salesforce-order/salesforce-order-control';
import { SalesforceOrderModal } from './shared/salesforce-order/salesforce-order-modal.presentation';
import { ManageEventNavigation } from './shared/navigation/navigation';
import { getNavigationLabels, SEPARATOR } from '../../../constants/navigation-labels.constants';
import { ReportsList } from './_reports/reports';
import { OpenFrom } from '../../../tracking-service/tracking-values';
import { AssetsList } from './_assets/assets';
import { ArtifactsList } from './_artifacts/artifacts';
import TroubleshootModalPresentation from './shared/troubleshoot-modal/troubleshoot-modal.presentation';

const RestrictedEventContent = (props: Omit<IRestrictedContentProps, 'intl'>) => (
    <RestrictedContent {...props} >
        <ManageEventNavigation />
    </RestrictedContent>
);

const ManageEventPresentation = mobxReact.observer((props: { vm: ManageEventViewModel }) => {
    const context = useContext(EventDetailsContext);
    const { vm } = props;
    const editPermission = hasPermission(Permission.EDIT_EVENT);
    const labels = getNavigationLabels(vm.intl);

    const event = vm.getEvent();

    let SpecificPage: React.ReactElement;
    switch (vm.getPage()) {
        case ManageEventPages.Schedule:
            SpecificPage = <RestrictedEventContent
                content={<MeetingsList vm={vm.getMeetingsListVM()}/>}
                permission={Permission.SCHEDULE_TAB}
            />;
            break;
        case ManageEventPages.Organizations:
            SpecificPage = <OrganizationsList
                vm={vm.getOrganizationsListVM()}
                attendeeListVM={vm.getAttendeesListVM()}
            />;
            break;
        case ManageEventPages.Attendees:
            SpecificPage = <AttendeesList vm={vm.getAttendeesListVM()}/>;
            break;
        case ManageEventPages.Rooms:
            SpecificPage = <RoomsList vm={vm.getRoomsListVM()}/>;
            break;
        case ManageEventPages.Connections:
            SpecificPage = <RestrictedEventContent
                content={
                    <ConnectionsList
                        roomsVM={vm.getRoomsListVM()}
                        vm={vm.getConnectionsListVM()}
                    />}
                permission={Permission.CONNECTIONS_MANAGEMENT}
            />;
            break;
        case ManageEventPages.Assets:
            SpecificPage = <RestrictedEventContent
                content={<AssetsList vm={vm.getAssetsVM()}/>}
                permission={Permission.ASSETS_TAB}
            />;
            break;
        case ManageEventPages.Artifacts:
            SpecificPage = <RestrictedEventContent
                content={<ArtifactsList vm={vm.getArtifactsVM()}/>}
                permission={Permission.ARTIFACTS_TAB}
            />;
            break;
        case ManageEventPages.Website:
            SpecificPage = <RestrictedEventContent
                content={<EditWebsite vm={vm.getEditWebsiteVM()}/>}
                permission={vm.getProperPassportPermission()}
            />;
            break;
        case ManageEventPages.Reports:
            SpecificPage = <RestrictedEventContent
                content={<ReportsList vm={vm.getReportsVM()} />}
                permission={Permission.REPORTS_TAB}
            />;
            break;
        case ManageEventPages.Control:
            SpecificPage = <ControlListPresentation
                vm={vm.getControlVM()}
                handleEventNotesUpdate={(eventNotes, openFrom: OpenFrom) => vm.handleEventNotesChange(eventNotes, openFrom)}
            />;
            break;
        default:
            SpecificPage = <Accounts/>;
            break;
    }

    useEffect(() => {
        context?.eventDetailsHandler(event);
    }, [event]);

    useEffect(() => {
        if (event?.name) {
            document.title = `${labels.ROOT} ${SEPARATOR} ${event?.name}`;
        }
    }, [event?.name]);

    if (vm.isLoadingEvent() || vm.getEvent() == null) {
        return <DefaultSpinner/>;
    }

    return (
        <SubWindowsHost>
            <div className='page-container'>
                <section className='event-title horizontal-row horizontal-row--with-bottom-margin'>
                    <h1 className='text-ellipsis event-name'>{event.name}</h1>
                    { event.oeNumber ? <h1 className='text-ellipsis event-name event__oe-number'> | OE {event.oeNumber}</h1> : null }
                    {
                        editPermission &&
                        <DefaultButton
                            className='visible-while-hovered'
                            modifier={['slim', 'link']}
                            text='Edit'
                            onClick={vm.editEvent}
                        />
                    }
                    <SalesforceOrderControl vm={vm.getSalesforceOrderVM()} />
                    <div className='horizontal-row__align-to-row-end event__time-container'>
                        <span className='secondary-time-container'>{vm.getFullFormattedTimezoneOffset()}</span>
                        <span className='event__time-stringified-day'>{vm.getFormattedEventDatesString()}</span>
                    </div>
                    <div className='tiny-event-title'>
                        {event.name} | {event.oeNumber && `OE ${event.oeNumber} |`} {vm.getFormattedEventDatesString()} | {vm.getFullFormattedTimezoneOffset()}
                    </div>
                </section>
                {SpecificPage}
                <SalesforceOrderModal vm={vm.getSalesforceOrderVM()} />
                <TroubleshootModalPresentation
                    vm={vm.getTroubleshootVM()}
                    intl={vm.intl}
                />
            </div>
        </SubWindowsHost>
    );
});

export { ManageEventPresentation };
